<script>
  import { onMount } from "svelte";
  import { apiRequest } from "../utils/api";

  export let isAdmin = false;
  export let selectedProjectId = null;   // URL-owned, from AppShell
  export let onSelectProject = () => {}; // onSelectProject(id): ask AppShell to select + push URL

  let projects = [];
  let loadingProjects = false;
  let loadedProjectId = null;

  let categories = [];
  let products = [];
  let loadingCatalog = false;
  let catalogError = null;
  let showDeleted = false;

  let categoryForm = { name: "", title_template: "", position: 1 };
  let editingCategoryId = null;
  let categoryMessage = null;
  let categoryError = null;
  let savingCategory = false;

  let importMessage = null;
  let importError = null;
  let importing = false;

  onMount(() => {
    if (isAdmin) {
      loadProjects();
    }
  });

  async function loadProjects() {
    loadingProjects = true;
    try {
      projects = await apiRequest("/api/projects");
    } catch (_error) {
      projects = [];
    } finally {
      loadingProjects = false;
    }
  }

  // The URL-owned project id flows in as a prop; load (or clear) the catalog in
  // response. The guard prevents double-loads and reactive loops.
  $: syncSelectedProject(selectedProjectId);
  function syncSelectedProject(id) {
    if (id && id !== loadedProjectId) {
      loadedProjectId = id;
      categoryMessage = null;
      categoryError = null;
      importMessage = null;
      importError = null;
      catalogError = null;
      resetCategoryForm();
      loadCatalog();
    } else if (!id && loadedProjectId !== null) {
      loadedProjectId = null;
      categories = [];
      products = [];
    }
  }

  async function loadCatalog() {
    if (!selectedProjectId) {
      return;
    }

    loadingCatalog = true;
    catalogError = null;
    try {
      const productsQuery = showDeleted ? "?include_deleted=1" : "";
      [categories, products] = await Promise.all([
        apiRequest(`/api/projects/${selectedProjectId}/product_categories`),
        apiRequest(`/api/projects/${selectedProjectId}/products${productsQuery}`)
      ]);
      resetCategoryForm();
    } catch (error) {
      catalogError = error?.message || "Could not load catalog.";
      categories = [];
      products = [];
    } finally {
      loadingCatalog = false;
    }
  }

  async function toggleShowDeleted() {
    showDeleted = !showDeleted;
    await loadCatalog();
  }

  async function deleteProduct(product) {
    if (!selectedProjectId) {
      return;
    }

    if (!window.confirm(`Delete product "${product.name}"? Past generations that used it keep working.`)) {
      return;
    }

    try {
      await apiRequest(`/api/projects/${selectedProjectId}/products/${product.id}`, {
        method: "DELETE"
      });
      await loadCatalog();
    } catch (error) {
      catalogError = error?.message || "Could not delete product.";
    }
  }

  function resetCategoryForm() {
    categoryForm = {
      name: "",
      title_template: "",
      position: (categories?.length || 0) + 1
    };
    editingCategoryId = null;
  }

  function editCategory(category) {
    editingCategoryId = category.id;
    categoryForm = {
      name: category.name,
      title_template: category.title_template,
      position: category.position
    };
    categoryMessage = null;
    categoryError = null;
  }

  async function saveCategory(event) {
    event?.preventDefault();
    if (!selectedProjectId || savingCategory || !categoryForm.name.trim()) {
      return;
    }

    savingCategory = true;
    categoryError = null;
    categoryMessage = null;

    const body = {
      product_category: {
        name: categoryForm.name.trim(),
        title_template: categoryForm.title_template.trim(),
        position: Number(categoryForm.position) || 1
      }
    };

    try {
      if (editingCategoryId) {
        await apiRequest(`/api/projects/${selectedProjectId}/product_categories/${editingCategoryId}`, {
          method: "PATCH",
          body
        });
        categoryMessage = "Category updated.";
      } else {
        await apiRequest(`/api/projects/${selectedProjectId}/product_categories`, {
          method: "POST",
          body
        });
        categoryMessage = "Category created.";
      }
      resetCategoryForm();
      await loadCatalog();
    } catch (error) {
      categoryError = error?.message || "Could not save category.";
    } finally {
      savingCategory = false;
    }
  }

  async function deleteCategory(category) {
    if (!selectedProjectId) {
      return;
    }

    const confirmMessage = `Delete category "${category.name}"? This is blocked if it still has products.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await apiRequest(`/api/projects/${selectedProjectId}/product_categories/${category.id}`, {
        method: "DELETE"
      });
      await loadCatalog();
    } catch (error) {
      catalogError = error?.message || "Could not delete category.";
    }
  }

  async function onFileChange(event) {
    const file = event.target.files?.[0];
    if (!file || !selectedProjectId || importing) {
      return;
    }

    importing = true;
    importError = null;
    importMessage = null;

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await apiRequest(`/api/projects/${selectedProjectId}/products/import`, {
        method: "POST",
        body: formData
      });

      const skipped = response.skipped || [];
      importMessage = `Imported ${response.imported}. Skipped ${skipped.length}.`;
      if (skipped.length) {
        importError = skipped.join("\n");
      }
      await loadCatalog();
    } catch (error) {
      importError = error?.message || "Import failed.";
    } finally {
      importing = false;
      event.target.value = "";
    }
  }
</script>

<div class="space-y-4">
  <div class="max-w-xs">
    <div class="form-group">
      <label class="form-label" for="catalog-proj-select">Project</label>
      {#if loadingProjects}
        <span class="text-xs text-neutral-500">Loading…</span>
      {:else}
        <select id="catalog-proj-select" class="form-input" on:change={(event) => onSelectProject(Number(event.target.value))} data-testid="catalog-project">
          <option value="" disabled selected={!selectedProjectId}>Pick a project…</option>
          {#each projects as project}
            <option value={project.id} selected={selectedProjectId === project.id}>{project.name}{project.project_category ? ` · ${project.project_category}` : ""}</option>
          {/each}
        </select>
      {/if}
    </div>
  </div>

  {#if !selectedProjectId}
    <p class="text-xs text-neutral-500">Select a project to manage its product catalog.</p>
  {:else if loadingCatalog}
    <div class="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start animate-pulse">
      <div class="lg:col-span-5 space-y-4">
        <div class="h-4 bg-neutral-200 rounded w-20"></div>
        <div class="h-32 bg-neutral-100 rounded-lg"></div>
        <div class="space-y-2">
          <div class="h-16 bg-white border border-neutral-200 rounded-lg"></div>
          <div class="h-16 bg-white border border-neutral-200 rounded-lg"></div>
        </div>
      </div>
      <div class="lg:col-span-7 space-y-4">
        <div class="h-4 bg-neutral-200 rounded w-16"></div>
        <div class="h-20 bg-neutral-100 rounded-lg"></div>
        <div class="h-32 bg-white border border-neutral-200 rounded-lg"></div>
      </div>
    </div>
  {:else}
    {#if catalogError}
      <p class="bg-danger-bg text-danger-text border border-danger-border/20 rounded px-3 py-1.5 text-xs" role="alert">{catalogError}</p>
    {/if}

    <div class="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
      <!-- Categories Column -->
      <section class="lg:col-span-5 space-y-4">
        <div class="space-y-1">
          <h3 class="text-xs font-bold text-neutral-800 tracking-wider">Categories</h3>
          <p class="text-[0.72rem] text-neutral-500">
            Each category owns a spending template. Use <code class="font-mono">{'{name}'}</code> where the product name goes.
          </p>
        </div>

        <form class="bg-neutral-50 border border-neutral-200 rounded-lg p-3.5 space-y-3" on:submit={saveCategory}>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div class="form-group">
              <label class="form-label" for="cat-name-in">Name</label>
              <input id="cat-name-in" class="form-input" required bind:value={categoryForm.name} placeholder="e.g. game" data-testid="cat-name" />
            </div>
            <div class="form-group">
              <label class="form-label" for="cat-pos-in">Position</label>
              <input id="cat-pos-in" class="form-input" type="number" min="1" bind:value={categoryForm.position} data-testid="cat-position" />
            </div>
          </div>
          
          <div class="form-group">
            <label class="form-label" for="cat-tmpl-in">Title Template</label>
            <input id="cat-tmpl-in" class="form-input" bind:value={categoryForm.title_template} placeholder="Played {name}" data-testid="cat-template" />
          </div>

          <div class="flex gap-2 justify-end pt-1">
            {#if editingCategoryId}
              <button type="button" class="btn-secondary py-1 text-xs" on:click={resetCategoryForm}>Cancel</button>
            {/if}
            <button type="submit" class="btn-primary py-1 text-xs" disabled={savingCategory}>
              {savingCategory ? "Saving..." : editingCategoryId ? "Update category" : "Add category"}
            </button>
          </div>
        </form>

        {#if categoryMessage}
          <p class="bg-success-bg text-success-text border border-success-border/20 rounded px-3 py-1.5 text-xs">{categoryMessage}</p>
        {/if}
        {#if categoryError}
          <p class="bg-danger-bg text-danger-text border border-danger-border/20 rounded px-3 py-1.5 text-xs" role="alert">{categoryError}</p>
        {/if}

        {#if categories.length === 0}
          <div class="bg-white border border-neutral-200 rounded-lg p-6 text-center space-y-2 shadow-2xs">
            <p class="text-neutral-500 text-xs font-semibold">No categories yet.</p>
            <p class="text-[0.7rem] text-neutral-400">Use the form above to add a new category to the project.</p>
          </div>
        {:else}
          <ul class="space-y-2">
            {#each categories as category}
              <li class="bg-white border border-neutral-200 rounded-lg p-3 flex flex-col gap-2 shadow-2xs">
                <div class="flex justify-between items-baseline gap-2">
                  <strong class="text-xs font-bold text-neutral-800 tracking-wider">{category.name}</strong>
                  <span class="text-[0.68rem] text-neutral-500">#{category.position} · {category.product_count} products</span>
                </div>
                <code class="text-[0.78rem] text-neutral-800 bg-neutral-50 rounded px-2 py-0.5 border border-neutral-200/60 font-mono">{category.title_template}</code>
                <div class="flex gap-2 justify-end mt-1">
                  <button type="button" class="btn-secondary py-0.5 px-2 text-[0.72rem]" on:click={() => editCategory(category)}>Edit</button>
                  <button type="button" class="btn-danger py-0.5 px-2 text-[0.72rem] bg-danger-bg text-danger-text border border-danger-border/20" on:click={() => deleteCategory(category)}>Delete</button>
                </div>
              </li>
            {/each}
          </ul>
        {/if}
      </section>

      <!-- Products Column -->
      <section class="lg:col-span-7 space-y-4">
        <h3 class="text-xs font-bold text-neutral-800 tracking-wider">Products</h3>
        
        <div class="bg-neutral-50 border border-neutral-200 rounded-lg p-3.5 space-y-2">
          <div class="form-group">
            <label class="form-label" for="csv-file-in">Import products (CSV)</label>
            <input
              id="csv-file-in"
              class="form-input file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-[0.72rem] file:font-semibold file:bg-brand-50 file:text-brand-700 hover:file:bg-brand-100"
              type="file"
              accept=".csv,text/csv"
              data-testid="csv-import"
              on:change={onFileChange}
              disabled={importing}
            />
          </div>
          <p class="text-[0.68rem] text-neutral-500">
            Columns: <code class="font-mono">category_name, name, pricing_type, amount, amount_eur, amount_min, amount_max, fixed_title</code>.
          </p>
          <p class="text-[0.68rem] text-neutral-500">
            <code class="font-mono">pricing_type</code>: <code class="font-mono">fixed</code> (uses amount + amount_eur) · <code class="font-mono">range</code> (uses amount_min + amount_max) · <code class="font-mono">free</code> (no amounts). <code class="font-mono">amount</code>/<code class="font-mono">amount_min</code>/<code class="font-mono">amount_max</code> are whole BOWS; <code class="font-mono">amount_eur</code> is decimal EUR; <code class="font-mono">fixed_title</code> is true/false. <code class="font-mono">category_name</code> must match an existing category.
          </p>
          <a
            href="/products-example.csv"
            download="products-example.csv"
            class="inline-block text-[0.68rem] text-brand-600 underline hover:text-brand-700"
            data-testid="products-import-example"
          >
            Download example CSV
          </a>
        </div>

        {#if importMessage}
          <p class="bg-success-bg text-success-text border border-success-border/20 rounded px-3 py-1.5 text-xs">{importMessage}</p>
        {/if}
        {#if importError}
          <pre class="bg-danger-bg text-danger-text border border-danger-border/20 rounded px-3 py-2 text-xs font-mono whitespace-pre-wrap">{importError}</pre>
        {/if}

        <label class="flex items-center gap-1.5 text-[0.72rem] text-neutral-600 cursor-pointer select-none">
          <input type="checkbox" class="form-checkbox" checked={showDeleted} on:change={toggleShowDeleted} data-testid="show-deleted-products" />
          Show deleted
        </label>

        {#if products.length === 0}
          <div class="bg-white border border-neutral-200 rounded-lg p-8 text-center space-y-2 shadow-2xs">
            <p class="text-neutral-500 text-xs font-semibold">No products yet.</p>
            <p class="text-[0.7rem] text-neutral-400">Import a CSV of products using the form above to populate the catalog.</p>
          </div>
        {:else}
          <div class="overflow-x-auto border border-neutral-200 rounded-lg">
            <table class="w-full text-left border-collapse text-xs">
              <thead>
                <tr class="bg-neutral-50 border-b border-neutral-200">
                  <th class="p-2.5 font-bold text-neutral-700">Name</th>
                  <th class="p-2.5 font-bold text-neutral-700">Category</th>
                  <th class="p-2.5 font-bold text-neutral-700">Pricing</th>
                  <th class="p-2.5 font-bold text-neutral-700">Price</th>
                  <th class="p-2.5 font-bold text-neutral-700">Fixed title</th>
                  <th class="p-2.5 font-bold text-neutral-700"></th>
                </tr>
              </thead>
              <tbody class="divide-y divide-neutral-100">
                {#each products as product}
                  <tr class="hover:bg-neutral-50/50 transition-colors" class:opacity-50={product.is_deleted} data-testid="product-row">
                    <td class="p-2.5 font-medium" class:text-neutral-900={!product.is_deleted} class:text-neutral-500={product.is_deleted} class:line-through={product.is_deleted}>
                      {product.name}
                      {#if product.is_deleted}
                        <span class="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-[0.62rem] font-bold uppercase bg-neutral-200 text-neutral-600" data-testid="product-deleted-tag">deleted</span>
                      {/if}
                    </td>
                    <td class="p-2.5 text-neutral-600" class:line-through={product.is_deleted}>{product.category || "—"}</td>
                    <td class="p-2.5">
                      <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[0.68rem] font-bold uppercase bg-neutral-100 text-neutral-600">
                        {product.pricing_type}
                      </span>
                    </td>
                    <td class="p-2.5 text-neutral-600" class:line-through={product.is_deleted}>
                      {#if product.pricing_type === "fixed"}
                        {product.amount} / €{product.amount_eur}
                      {:else if product.pricing_type === "range"}
                        {product.amount_min}–{product.amount_max}
                      {:else}
                        free
                      {/if}
                    </td>
                    <td class="p-2.5 text-neutral-600" class:line-through={product.is_deleted}>{product.fixed_title ? "yes" : "no"}</td>
                    <td class="p-2.5 text-right">
                      {#if !product.is_deleted}
                        <button type="button" class="btn-danger py-0.5 px-2 text-[0.72rem] bg-danger-bg text-danger-text border border-danger-border/20" title="Delete product" data-testid="delete-product" on:click={() => deleteProduct(product)}>×</button>
                      {/if}
                    </td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>
        {/if}
      </section>
    </div>

    <!-- Reference legend -->
    <section class="border-t border-neutral-200 pt-4 mt-2 space-y-3">
      <h3 class="text-xs font-bold text-neutral-800 tracking-wider">Reference</h3>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
        <div class="space-y-1">
          <p class="text-[0.72rem] font-bold text-neutral-700">What is a category?</p>
          <p class="text-[0.7rem] text-neutral-500 leading-relaxed">
            A category groups products and owns a <strong>title template</strong> — the label a spending row shows in the statement, with <code class="font-mono">{'{name}'}</code> filled in from the product (e.g. <code class="font-mono">Played {'{name}'}</code> → <code class="font-mono">Played Chess</code>). Every product belongs to one category.
          </p>
        </div>

        <div class="space-y-1">
          <p class="text-[0.72rem] font-bold text-neutral-700">
            <code class="font-mono">pricing_type</code> — how the spend amount is set
          </p>
          <ul class="text-[0.7rem] text-neutral-500 leading-relaxed space-y-0.5">
            <li><code class="font-mono">fixed</code> — one set price: <code class="font-mono">amount</code> (BOWS) + <code class="font-mono">amount_eur</code> (EUR).</li>
            <li><code class="font-mono">range</code> — a band <code class="font-mono">amount_min</code>–<code class="font-mono">amount_max</code> (BOWS); the amount is chosen inside it per row.</li>
            <li><code class="font-mono">free</code> — no catalog price; the amount is supplied per row.</li>
          </ul>
        </div>

        <div class="space-y-1 md:col-span-2">
          <p class="text-[0.72rem] font-bold text-neutral-700">
            <code class="font-mono">fixed_title</code> — is the row’s title locked? (a <em>different</em> thing from pricing)
          </p>
          <p class="text-[0.7rem] text-neutral-500 leading-relaxed">
            <code class="font-mono">true</code> (default) — the title is derived from the category template and locked; any per-row description is ignored.
            <code class="font-mono">false</code> — the title is free text supplied per row (e.g. delivery, tournament) and passes through unchanged.
          </p>
          <p class="text-[0.7rem] text-amber-700 bg-amber-50 border border-amber-200/60 rounded px-2 py-1 leading-relaxed">
            Heads-up on the naming clash: <code class="font-mono">pricing_type: fixed</code> (a <strong>price</strong> that doesn’t vary) is unrelated to <code class="font-mono">fixed_title: true</code> (a <strong>title</strong> that doesn’t vary). A product can be any combination — e.g. <code class="font-mono">free</code> price with a <code class="font-mono">fixed</code> title.
          </p>
        </div>
      </div>

      <details class="border border-neutral-200 rounded-lg" data-testid="field-hint-details">
        <summary class="cursor-pointer select-none px-3 py-2 text-[0.72rem] font-bold text-neutral-700 tracking-wider">
          Field reference: how the model sees each field, what it enforces, what's required
        </summary>
        <div class="border-t border-neutral-200 p-3 overflow-x-auto">
          <p class="text-[0.68rem] text-neutral-500 mb-2 leading-relaxed">
            Catalog products are created via <strong>CSV import only</strong> (no per-field create form), so "required" below means required CSV column / model validation.
          </p>
          <table class="w-full text-left border-collapse text-[0.7rem]">
            <thead>
              <tr class="border-b border-neutral-200">
                <th class="p-2 font-bold text-neutral-700">Field</th>
                <th class="p-2 font-bold text-neutral-700">👁 get_generation_info</th>
                <th class="p-2 font-bold text-neutral-700">🚧 post_transaction</th>
                <th class="p-2 font-bold text-neutral-700">✅ required</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-neutral-100 text-neutral-600 leading-relaxed align-top">
              <tr>
                <td class="p-2 font-mono font-medium text-neutral-800">name</td>
                <td class="p-2"><code class="font-mono">product_catalog[].name</code> (exact ref for <code class="font-mono">row.product</code>)</td>
                <td class="p-2">matched by id or exact name → unknown rejected; fills <code class="font-mono">{'{name}'}</code> in the title for fixed-title</td>
                <td class="p-2">yes (model presence; CSV column)</td>
              </tr>
              <tr>
                <td class="p-2 font-mono font-medium text-neutral-800">category_name</td>
                <td class="p-2"><code class="font-mono">product_catalog[].category</code> + <code class="font-mono">category_id</code>; also <code class="font-mono">product_categories[]</code> w/ <code class="font-mono">title_template</code></td>
                <td class="p-2">builds the fixed-title description; drives allow_list / battle / ordering rules</td>
                <td class="p-2">yes — must match an existing category (CSV: unknown → row skipped)</td>
              </tr>
              <tr>
                <td class="p-2 font-mono font-medium text-neutral-800">pricing_type</td>
                <td class="p-2"><code class="font-mono">product_catalog[].pricing_type</code></td>
                <td class="p-2"><code class="font-mono">fixed</code>→catalog price (client amount ignored); <code class="font-mono">range</code>→amount within min–max; <code class="font-mono">free</code>→positive amount required</td>
                <td class="p-2">optional (default <code class="font-mono">fixed</code>)</td>
              </tr>
              <tr>
                <td class="p-2 font-mono font-medium text-neutral-800">amount</td>
                <td class="p-2"><code class="font-mono">product_catalog[].amount</code> (fixed only)</td>
                <td class="p-2">fixed→catalog; range/free→client amount used + validated</td>
                <td class="p-2">required only for <code class="font-mono">fixed</code> (catalog); from client for range/free</td>
              </tr>
              <tr>
                <td class="p-2 font-mono font-medium text-neutral-800">amount_eur</td>
                <td class="p-2"><code class="font-mono">product_catalog[].amount_eur</code></td>
                <td class="p-2">fixed→catalog; range/free→client or derived = amount/rate; category_consistency checks amount≈eur×rate</td>
                <td class="p-2">required only for <code class="font-mono">fixed</code>; per-row optional (derived)</td>
              </tr>
              <tr>
                <td class="p-2 font-mono font-medium text-neutral-800">amount_min / amount_max</td>
                <td class="p-2"><code class="font-mono">product_catalog[].amount_min/max</code> (range only)</td>
                <td class="p-2">band for <code class="font-mono">range</code>; client amount must be inside</td>
                <td class="p-2">required only for <code class="font-mono">range</code> (both)</td>
              </tr>
              <tr>
                <td class="p-2 font-mono font-medium text-neutral-800">fixed_title</td>
                <td class="p-2"><code class="font-mono">product_catalog[].fixed_title</code></td>
                <td class="p-2"><code class="font-mono">true</code>→server-derived title (client desc ignored); <code class="font-mono">false</code>→client description required</td>
                <td class="p-2">optional (default <code class="font-mono">true</code>)</td>
              </tr>
            </tbody>
          </table>
        </div>
      </details>
    </section>
  {/if}
</div>
